/**
 * UploadLimits.jsx — Configurable per-segment file upload limits (super_admin only)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Save, RotateCcw, Info } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut, apiDel } from '../../lib/api';

function groupBy(arr, key) {
  return arr.reduce((acc, item) => {
    (acc[item[key]] = acc[item[key]] || []).push(item);
    return acc;
  }, {});
}

export default function UploadLimits() {
  const [segments, setSegments] = useState([]);
  const [edits, setEdits] = useState({});    // key → string input value
  const [saving, setSaving] = useState(false);
  const MAX_CEILING = 200;

  const load = useCallback(async () => {
    try {
      const r = await apiGet('/settings/upload-limits');
      setSegments(r.segments || []);
      const init = {};
      (r.segments || []).forEach(s => { init[s.key] = String(s.effective_mb); });
      setEdits(init);
    } catch { toast.error('Failed to load upload limits'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const overrides = {};
      segments.forEach(s => {
        const val = parseInt(edits[s.key], 10);
        if (!isNaN(val) && val !== s.default_mb) overrides[s.key] = val;
      });
      await apiPut('/settings/upload-limits', { overrides });
      toast.success('Upload limits saved');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const reset = async (key) => {
    try {
      await apiDel(`/settings/upload-limits/${key}`);
      toast.success('Reset to default');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
  };

  const grouped = groupBy(segments, 'module');

  return (
    <div className="max-w-3xl space-y-6" data-testid="upload-limits-page">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-display font-bold text-lg">File Upload Limits</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Configure per-segment size caps. BD & Tendering default to 100 MB. Hard server ceiling: {MAX_CEILING} MB.
          </p>
        </div>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold hover:bg-[#2c3658] disabled:opacity-50"
          data-testid="upload-limits-save">
          <Save size={13} /> {saving ? 'Saving…' : 'Save All'}
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-2.5 text-xs text-amber-800 flex items-start gap-2">
        <Info size={13} className="shrink-0 mt-0.5" />
        <span>
          Changes apply immediately — no restart needed. Values above {MAX_CEILING} MB will be rejected.
          Your proxy/platform may also have its own body-size cap — contact Emergent Support if uploads still fail above 100 MB.
        </span>
      </div>

      {Object.entries(grouped).map(([mod, segs]) => (
        <div key={mod} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
            <span className="text-xs font-display font-bold uppercase tracking-wider text-slate-600">{mod}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {segs.map(s => {
              const val = edits[s.key] ?? String(s.effective_mb);
              const num = parseInt(val, 10);
              const isChanged = !isNaN(num) && num !== s.default_mb;
              const isError = isNaN(num) || num < 1 || num > MAX_CEILING;
              return (
                <div key={s.key} className="flex items-center gap-4 px-4 py-2.5">
                  <div className="flex-1">
                    <div className="text-xs font-display font-semibold text-slate-700">{s.label}</div>
                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">{s.key} · default: {s.default_mb} MB</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="1" max={MAX_CEILING} step="5"
                      value={val}
                      onChange={e => setEdits(prev => ({ ...prev, [s.key]: e.target.value }))}
                      className={`w-20 border rounded-sm px-2 py-1 text-sm text-right focus:outline-none focus:border-[#3b4872] ${isError ? 'border-rose-400' : isChanged ? 'border-amber-400' : 'border-slate-300'}`}
                      data-testid={`ul-input-${s.key}`}
                    />
                    <span className="text-xs text-slate-500 w-4">MB</span>
                    {s.is_override && (
                      <button onClick={() => reset(s.key)} title="Reset to default"
                        className="text-slate-400 hover:text-rose-600" data-testid={`ul-reset-${s.key}`}>
                        <RotateCcw size={13} />
                      </button>
                    )}
                    {!s.is_override && <div className="w-5" />}
                    {isChanged && !isError && (
                      <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">changed</span>
                    )}
                    {isError && (
                      <span className="text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-bold">invalid</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
